import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import Sidebar from "./Sidebar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const Audiences = () => {
  const [filter, setFilter] = useState("All Audiences");
  const [openModal, setOpenModal] = useState(false);
  const [newAudience, setNewAudience] = useState({
    name: "",
    type: "Dynamic",
    filters: {
      location: "",
      activity: false,
      demographics: "",
      orders: { operator: "", value: "" },
      aov: { operator: "", value: "" },
      timeSinceLastOrder: { operator: "", value: "" },
      churnRisk: false,
      customerType: "",
    },
  });
  const [audiences, setAudiences] = useState([
    { name: "High-Value Customers", size: "1,234", type: "Dynamic", created: "2025-01-01" },
    { name: "Cart Abandoners", size: "890", type: "Static", created: "2024-12-15" },
  ]);
  const [segmentFilters, setSegmentFilters] = useState({
    orders: { operator: "", value: "" },
    aov: { operator: "", value: "" },
    timeSinceLastOrder: { operator: "", value: "" },
    churnRisk: false,
    customerType: "",
  });
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleFilterChange = (event) => setFilter(event.target.value);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (type === "checkbox") {
      setNewAudience((prev) => ({
        ...prev,
        filters: { ...prev.filters, [name]: checked },
      }));
    } else {
      setNewAudience((prev) => ({
        ...prev,
        [name]: value,
        filters: { ...prev.filters, [name]: value },
      }));
    }
  };

  const handleSegmentInputChange = (e, field, subField) => {
    const { value } = e.target;
    setSegmentFilters((prev) => ({
      ...prev,
      [field]: { ...prev[field], [subField]: value },
    }));
  };

  const handleSaveAudience = () => {
    const newAudienceEntry = {
      name: newAudience.name,
      size: Math.floor(Math.random() * 1000 + 500).toString(),
      type: newAudience.type,
      created: new Date().toISOString().split("T")[0],
    };
    setAudiences((prev) => [...prev, newAudienceEntry]);
    setNewAudience({
      name: "",
      type: "Dynamic",
      filters: {
        location: "",
        activity: false,
        demographics: "",
        orders: { operator: "", value: "" },
        aov: { operator: "", value: "" },
        timeSinceLastOrder: { operator: "", value: "" },
        churnRisk: false,
        customerType: "",
      },
    });
    handleCloseModal();
  };

  const handleDeleteAudience = (index) => {
    setAudiences((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApplySegmentation = async () => {
    setLoadingPreview(true);
    try {
      const response = await fetch("/api/segment-audience", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(segmentFilters),
      });
      const data = await response.json();
      setPreviewData(data || []);
    } catch (error) {
      console.error("Error fetching segmentation preview:", error);
      setPreviewData([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await fetch("/api/download-segment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(segmentFilters),
      });
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "segmentation_preview.csv";
      link.click();
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const filteredAudiences =
    filter === "All Audiences"
      ? audiences
      : audiences.filter((audience) => audience.type === filter);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f9f9f9" }}>
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, padding: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">Audiences</Typography>
          <Button variant="contained" onClick={handleOpenModal}>
            Create New Audience
          </Button>
        </Box>

        {/* Filter */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <FormControl variant="outlined" size="small">
            <InputLabel>Filter</InputLabel>
            <Select value={filter} onChange={handleFilterChange} label="Filter">
              <MenuItem value="All Audiences">All Audiences</MenuItem>
              <MenuItem value="Dynamic">Dynamic Audiences</MenuItem>
              <MenuItem value="Static">Static Audiences</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Audiences Table */}
        <Paper sx={{ padding: 3, borderRadius: 2 }}>
          <Typography variant="h6">Audience List</Typography>
          <Divider sx={{ mb: 2 }} />
          {filteredAudiences.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Audience Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAudiences.map((audience, index) => (
                    <TableRow key={index}>
                      <TableCell>{audience.name}</TableCell>
                      <TableCell>{audience.size}</TableCell>
                      <TableCell>{audience.type}</TableCell>
                      <TableCell>{audience.created}</TableCell>
                      <TableCell>
                        <IconButton>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteAudience(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
              No audiences found.
            </Typography>
          )}
        </Paper>

        {/* Segmentation Wizard */}
        <Paper sx={{ padding: 3, borderRadius: 2, mt: 3, backgroundColor: "#f3f3f3" }}>
          <Typography variant="h6">Segment Existing Customers</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
           {/* Number of Orders */}
            <Typography>Number of Orders</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl sx={{ minWidth: 120, zIndex: 1300 }} size="small">
                <InputLabel>Operator</InputLabel>
                <Select
                  value={segmentFilters.orders.operator}
                  onChange={(e) => handleSegmentInputChange(e, "orders", "operator")}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 200, // Prevents it from being too tall
                            overflow: "auto",
                            zIndex: 1500, // Ensures it's above everything
                          },
                        },
                      }}
                >
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="number"
                size="small"
                placeholder="Value"
                value={segmentFilters.orders.value}
                onChange={(e) => handleSegmentInputChange(e, "orders", "value")}
              />
            </Box>

            {/* AOV */}
            <Typography>Average Order Value (AOV)</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Operator</InputLabel>
                <Select
                  value={segmentFilters.aov.operator}
                  onChange={(e) => handleSegmentInputChange(e, "aov", "operator")}
                >
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="number"
                size="small"
                placeholder="Value"
                value={segmentFilters.aov.value}
                onChange={(e) => handleSegmentInputChange(e, "aov", "value")}
              />
            </Box>

            {/* Time Since Last Order */}
            <Typography>Time Since Last Order</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl sx={{ minWidth: 120 }} variant="outlined" size="small">
                <InputLabel shrink>Operator</InputLabel>
                <Select
                  value={segmentFilters.timeSinceLastOrder.operator}
                  onChange={(e) => handleSegmentInputChange(e, "timeSinceLastOrder", "operator")}
                >
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value=">=">&gt;=</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value="<=">&lt;=</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="number"
                size="small"
                placeholder="Value"
                value={segmentFilters.timeSinceLastOrder.value}
                onChange={(e) => handleSegmentInputChange(e, "timeSinceLastOrder", "value")}
              />
            </Box>

            {/* Churn Risk */}
            <FormControlLabel
              control={
                <Checkbox
                  name="churnRisk"
                  checked={segmentFilters.churnRisk}
                  onChange={(e) => setSegmentFilters((prev) => ({ ...prev, churnRisk: e.target.checked }))}
                />
              }
              label="Churn Risk"
            />

            {/* Customer Type */}
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Customer Type</InputLabel>
              <Select
                name="customerType"
                value={segmentFilters.customerType}
                onChange={(e) => setSegmentFilters((prev) => ({ ...prev, customerType: e.target.value }))}
                label="Customer Type"
              >
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Returning">Returning</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="contained" onClick={handleApplySegmentation} disabled={loadingPreview}>
                {loadingPreview ? "Loading..." : "Preview Data"}
              </Button>
              <Button variant="outlined" onClick={handleDownloadCSV} disabled={previewData.length === 0}>
                Download CSV
              </Button>
            </Box>
          </Box>

          {/* Preview Data Table */}
          {previewData.length > 0 ? (
            <Paper sx={{ marginTop: 3, padding: 2 }}>
              <Typography variant="h6">Preview Data</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Orders</TableCell>
                      <TableCell>AOV</TableCell>
                      <TableCell>Last Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.orders}</TableCell>
                        <TableCell>{row.aov}</TableCell>
                        <TableCell>{row.lastOrder}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <Typography sx={{ textAlign: "center", mt: 2 }}>
              Preview data will appear here after applying segmentation.
            </Typography>
          )}
        </Paper>

        {/* Create New Audience Modal */}
        <Modal open={openModal} onClose={handleCloseModal} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Paper sx={{ padding: 3, width: 500, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Create New Audience
            </Typography>
            <TextField
              fullWidth
              label="Audience Name"
              variant="outlined"
              name="name"
              value={newAudience.name}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select name="type" value={newAudience.type} onChange={handleInputChange} label="Type">
                <MenuItem value="Dynamic">Dynamic</MenuItem>
                <MenuItem value="Static">Static</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Location"
              variant="outlined"
              name="location"
              value={newAudience.filters.location}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="activity"
                  checked={newAudience.filters.activity}
                  onChange={handleInputChange}
                />
              }
              label="Target Active Users"
            />
            <TextField
              fullWidth
              label="Demographics"
              variant="outlined"
              name="demographics"
              value={newAudience.filters.demographics}
              onChange={handleInputChange}
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <Button variant="outlined" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveAudience}>
                Save
              </Button>
            </Box>
          </Paper>
        </Modal>
      </Box>
    </Box>
  );
};

export default Audiences;
